import React from 'react';
import styled from 'styled-components';

import {unique} from '../../lib/helpers';
import Card from '../../components/Card';
import Icon from '../../components/Icon';
import Button from '../../components/Button';
import Notification from '../../components/Notification';

import {getValidationErrors, flattenValidationErrors} from './shared';

function Checklist({form, notifications, disabled, saving, isNewForm, validationEnabled}) {
  let validations = getValidationErrors(form, {disabled: !validationEnabled});
  let tmpValidations = validations;

  if (!validationEnabled) {
    // If validations are disabled, we still need to know if any section of the form has errors to show the appropriate
    // check mark. When validations are disabled, we will force their calculation.
    tmpValidations = getValidationErrors(form);
  }

  const nameErrors = flattenValidationErrors(validations.name);
  const tmpNameErrors = validations === tmpValidations 
    ? nameErrors 
    : flattenValidationErrors(tmpValidations.name);
  const questionErrors = [...flattenValidationErrors(validations.questions), ...flattenValidationErrors(validations.questionsById)];
  const tmpQuestionErrors = validations === tmpValidations
    ? questionErrors
    : [...flattenValidationErrors(tmpValidations.questions), ...flattenValidationErrors(tmpValidations.questionsById)];
  // Section-level question errors (not tied to a single question) vs. per-question errors we can jump to.
  const generalQuestionErrors = flattenValidationErrors(validations.questions);
  const questionLookup = [...form.contextQuestions, ...form.questions].reduce((map, question) => {
    map[question.id] = question;
    return map;
  }, {});
  const brokenQuestions = Object.keys(validations.questionsById || {})
    .map(id => ({
      id,
      question: questionLookup[id],
      errors: unique(flattenValidationErrors(validations.questionsById[id]))
    }))
    .filter(entry => entry.errors.length > 0);
  const alertErrors = flattenValidationErrors(validations.alertsById);
  const contextAlertErrors = flattenValidationErrors(validations.contextAlertsById);
  const tmpAlertErrors = validations === tmpValidations
    ? alertErrors
    : flattenValidationErrors(tmpValidations.alertsById);
  const permissionErrors = flattenValidationErrors(validations.reviewers);
  const tmpPermissionErrors = validations === tmpValidations
    ? permissionErrors
    : flattenValidationErrors(tmpValidations.reviewers);
  const tmpNotificationErrors = flattenValidationErrors(validations.notificationExpressionErrors)

  return (
    <Container>
      <Card>
        <PinnedTitle>Form Checklist</PinnedTitle>
        <ScrollArea>
          <ChecklistItem
            title="1. Form Name"
            anchor="#form-name"
            errors={unique(nameErrors)}
            complete={tmpNameErrors.length === 0}
          />
          <ChecklistItem
            title="2. Questions"
            anchor="#questions"
            errors={unique(generalQuestionErrors)}
            questionErrors={brokenQuestions}
            complete={tmpQuestionErrors.length === 0}
          />
          <ChecklistItem
            title="3. Permissions"
            anchor="#permissions"
            errors={unique(permissionErrors)}
            complete={tmpPermissionErrors.length === 0}
          />
          <ChecklistItem
            title="4. Notifications"
            anchor="#notifications"
            errors={unique(tmpNotificationErrors)}
            complete={!isNewForm || (notifications.length > 0 && tmpNotificationErrors.length ===0)}
          />
          <ChecklistItem
            title="5. Alerts"
            anchor="#alerts"
            errors={unique(alertErrors)}
            complete={!isNewForm || (form.alerts.length > 0 && tmpAlertErrors.length === 0)}
          />
          <ChecklistItem
            title="6. ContextAlerts"
            anchor="#alerts"
            errors={unique(contextAlertErrors)}
            complete={!isNewForm || (form.contextAlerts.length > 0 && tmpAlertErrors.length === 0)}
          />
          <ChecklistButton>
            <Button
              block
              disabled={disabled || saving}
              type="submit">
              {saving ? 'Saving...' : (isNewForm ? 'Create Form' : 'Save Form')}
            </Button>
          </ChecklistButton>
        </ScrollArea>
      </Card>
    </Container>
  );
}

const Container = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 25rem;
  padding: 1.875rem 1.875rem 1.875rem 0;

  @media (max-width: 75rem) {
    display: none;
  }
`;

// The title sits above the scroll area, so it stays visible without the card
// having to claim the full screen height.
const PinnedTitle = styled(Card.Title)`
  flex: 0 0 auto;
`;

// Scrolls the checklist aside from the title
const ScrollArea = styled.div`
  max-height: calc(100vh - 8rem);
  overflow-y: auto;

  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE / legacy Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome / Safari */
  }
`;

function jumpToQuestion(id) {
  const el = document.getElementById(`question-${id}`);

  if (el) {
    el.scrollIntoView({behavior: 'smooth', block: 'center'});
  }
}

function questionLabel(question) {
  return question && question.text && question.text.trim() ? question.text : 'Untitled question';
}

const ChecklistItem = styled(({title, anchor, complete, errors = [], questionErrors = [], ...props}) => {
  const hasErrors = errors.length > 0 || questionErrors.length > 0;

  return (
    <div {...props}>
      <ChecklistIcon complete={!hasErrors && complete} error={hasErrors} />
      <ChecklistItemBody>
        <a href={anchor}>{title}</a>
        {errors.length > 0 && (
          <Notification mini noIcon error>
            <ul>
              {errors.map(error => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </Notification>
        )}
        {questionErrors.length > 0 && (
          <Notification mini noIcon error>
            <ul>
              {questionErrors.map(entry => (
                <li key={entry.id}>
                  <QuestionJumpLink type="button" onClick={() => jumpToQuestion(entry.id)}>
                    {questionLabel(entry.question)}
                  </QuestionJumpLink>
                  <ul>
                    {entry.errors.map(error => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </Notification>
        )}
      </ChecklistItemBody>
    </div>
  );
})`
  padding: 0.5rem 1.875rem;
  display: flex;
`;

const QuestionJumpLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: #0049ee;
  text-align: left;
  text-decoration: underline;
  cursor: pointer;
`;

const ChecklistItemBody = styled.div`
  flex: 1;
  padding: 0.5rem 0 0 0;

  > a {
    display: block;
    margin-bottom: 0.5rem;
  }

  ul {
    font-size: 0.875rem;

    /* Changed to Full dots for easier visibility */
    ul {
      list-style-type: disc;

      li::marker {
        color: #000;
        font-weight: bold;
      }
    }
  }
`;

const ChecklistIcon = styled(({complete, error, ...props}) => (
  <div {...props}>
    <Icon name={error ? 'alert-circle' : 'check'} size="1.5em" />
  </div>
))`
  padding: 0.5rem;
  background: ${props => props.complete ? '#0049ee' : props.error ? '#B4131A' : '#b2b2b2'};
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  color: #fff;
  margin-right: 0.75rem;
`;

const ChecklistButton = styled.div`
  padding: 1.25rem 1.875rem;
`;

export default Checklist;